import { DEFAULT_LANDING } from '~/utils/authNavigation'

/**
 * Status-page logic — the pure decisions behind the app's error / access
 * screens: the Nuxt root handler `error.vue` (404 / 401 / 500 / dev) and the
 * `/account/unauthorized` (403) page. Kept framework-free so the status→view
 * mapping is unit-tested without mounting Vuetify or faking a NuxtError.
 *
 * These screens are CLIENT-ONLY (there is no server counterpart): a 403 is the
 * `decideRouteAccess` "unauthorized" verdict the auth middleware redirects
 * here, a 404 is an unmatched Nuxt route, and a 500 is a render/runtime error.
 */

/** The illustration keys the status pages use (mapped to assets in the page). */
export const STATUS_IMAGE = {
  UNAUTHORIZED: 'misc401',
  NOT_FOUND: 'misc404',
}

/**
 * Maps a Nuxt error to what the error page shows: title, description, which
 * illustration, and whether a "Try again" action makes sense. `isDev` surfaces
 * the raw error in development; production stays friendly.
 */
export function resolveErrorView(error, { isDev = false } = {}) {
  const code = error?.statusCode ?? null
  const is404 = code === 404 || String(error?.message ?? '').includes('404')

  let content
  if (is404) {
    content = {
      title: 'Page Not Found',
      description:
        "We couldn't find the page you're looking for. It might have been moved or removed.",
    }
  } else if (code === 401) {
    content = {
      title: 'Unauthorized',
      description: "You don't have permission to access this page.",
    }
  } else if (isDev) {
    content = {
      title: error?.statusMessage || 'Error',
      description: error?.message || 'An unexpected error occurred.',
    }
  } else {
    content = {
      title: 'Something went wrong',
      description: "We're working on it and we'll get it fixed as soon as we can.",
    }
  }

  return {
    code,
    title: content.title,
    description: content.description,
    image: code === 401 ? STATUS_IMAGE.UNAUTHORIZED : STATUS_IMAGE.NOT_FOUND,

    // Retrying only helps a transient/server error (or while debugging in dev);
    // a 404 / 403 won't fix itself on reload.
    showTryAgain: Boolean(isDev || (code && code >= 500)),
  }
}

/**
 * "Go back" for a dead-end status page: return to history when there IS a
 * previous entry, otherwise replace to the landing page so the button never
 * dead-ends (a direct hit / fresh tab has nowhere to go back to). The fallback
 * defaults to the app's single home constant, `DEFAULT_LANDING`.
 */
export function resolveBackNavigation({ canGoBack = false, fallback = DEFAULT_LANDING } = {}) {
  return canGoBack ? { type: 'back' } : { type: 'replace', to: fallback }
}
